import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Copy,
  ExternalLink,
  FileCheck2,
  FileJson2,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Play,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  UploadCloud
} from 'lucide-react';
import GuestBlogPreview from '../GuestBlogPreview.jsx';
import ProductVideoFeature from '../ProductVideoFeature.jsx';
import MetadataStudioStory from './MetadataStudioStory.jsx';
import HomeFlutedGlass from './HomeFlutedGlass.jsx';
import HomeShowcaseIntro from './showcase/HomeShowcaseIntro.jsx';
import HomeShowcaseLanguages from './showcase/HomeShowcaseLanguages.jsx';
import HomeShowcaseReels from './showcase/HomeShowcaseReels.jsx';
import LoadingState from '../ui/LoadingState.jsx';
import { localizeSeoHref } from '../../utils/localeRouting.js';
import { getMetadataStudioThemeAssets } from '../../data/metadataStudioAssets.js';
import { getLanguageMarket } from '../../locales/index.js';
import '../../styles/PremiumPublicHome.css';
import '../../styles/HomeShowcase.css';

const OUTPUT_FORMATS = ['CSV', 'XMP', 'JSON'];
const COMPARISON_HIKER_SRC = '/home-redesign/story/optimized/alpine-hiker-640.webp';

const exportPreviewCopy = (sample, format) => {
  if (!sample) return '';
  const title = sample.title || '';
  const description = sample.description || '';
  const keywords = Array.isArray(sample.keywords) ? sample.keywords : [];
  const csvEscape = (value) => `"${String(value).replaceAll('"', '""')}"`;
  const xmlEscape = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');

  if (format === 'CSV') {
    return [
      'filename,title,description,keywords',
      [sample.mediaLabel || 'asset', title, description, keywords.join(', ')].map(csvEscape).join(',')
    ].join('\n');
  }

  if (format === 'XMP') {
    return [
      '<x:xmpmeta xmlns:x="adobe:ns:meta/">',
      '  <rdf:Description xmlns:dc="http://purl.org/dc/elements/1.1/">',
      `    <dc:title>${xmlEscape(title)}</dc:title>`,
      `    <dc:description>${xmlEscape(description)}</dc:description>`,
      `    <dc:subject>${keywords.map(xmlEscape).join(', ')}</dc:subject>`,
      '  </rdf:Description>',
      '</x:xmpmeta>'
    ].join('\n');
  }

  return JSON.stringify({
    type: String(sample.mediaLabel || 'asset').toLowerCase(),
    title,
    description,
    keywords
  }, null, 2);
};

const SectionIntro = ({ eyebrow, title, description, align = 'left' }) => (
  <header className={`premium-section-intro premium-section-intro--${align}`}>
    {eyebrow ? <p className="premium-eyebrow">{eyebrow}</p> : null}
    <h2>{title}</h2>
    {description ? <p className="premium-section-description">{description}</p> : null}
  </header>
);

const ProductPicture = ({
  src,
  srcSet,
  alt,
  width,
  height,
  loading = 'lazy',
  fetchPriority,
  sizes = '(max-width: 760px) calc(100vw - 40px), (max-width: 1180px) 82vw, 760px',
  className = ''
}) => (
  <picture className={className}>
    <img
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      fetchpriority={fetchPriority}
    />
  </picture>
);

const IconCard = ({ item, index }) => {
  const Icon = item.icon || Sparkles;
  return (
    <article className="premium-icon-card" data-parity-item={index !== undefined ? index + 1 : undefined}>
      <span className="premium-icon-card-icon" aria-hidden="true"><Icon size={20} /></span>
      <h3>{item.title}</h3>
      <p>{item.description}</p>
    </article>
  );
};

const HeroMetadataTransformation = ({ ui }) => (
  <div className="premium-hero-transform" role="group" aria-label={ui.heroReadyFor}>
    <article className="premium-hero-raw-file">
      <div className="premium-hero-raw-image">
        <img
          src="/home-redesign/story/optimized/alpine-hiker-640.webp"
          alt=""
          width="640"
          height="427"
          loading="eager"
          decoding="async"
        />
        <span><ImageIcon size={12} aria-hidden="true" />JPG</span>
      </div>
      <div>
        <small>{ui.heroInputLabel}</small>
        <strong>{ui.heroFileName}</strong>
      </div>
    </article>

    <div className="premium-hero-processing" aria-hidden="true">
      <span />
      <em>{ui.heroAnalyze}</em>
    </div>

    <article className="premium-hero-result">
      <div className="premium-hero-result-head">
        <span><Sparkles size={13} aria-hidden="true" />{ui.heroTitleReady}</span>
        <strong><CheckCircle2 size={13} aria-hidden="true" />{ui.heroReadyFor}</strong>
      </div>
      <p>{ui.heroSampleTitle}</p>
      <div>{ui.heroKeywords?.map((keyword, index) => <span key={keyword}><b>{index + 1}</b>{keyword}</span>)}</div>
    </article>
  </div>
);

const PremiumPublicHomeLanding = ({
  language = 'en',
  theme = 'light',
  themeReady = false,
  publicHomeUiCopy,
  metadataStoryCopy,
  homeShowcaseCopy,
  renderActionElement,
  homeContent,
  metadataCapabilityContent,
  homeVideoCopy,
  studioVideo,
  salesCopy,
  showAuthenticatedUi,
  showPricingPrompt,
  isFreeMember,
  isUnlimitedPlan,
  studioHref,
  demoHref,
  tourHref,
  pricingHref,
  demoStarted,
  onScrollToStudio,
  onStartDemo,
  onOpenTour,
  onOpenLogin,
  onOpenPricing,
  localizedStats = [],
  localizedMetadataFlowSteps = [],
  localizedMetadataCapabilityCards = [],
  metadataFlowIcons = [],
  metadataCapabilityCardIcons = [],
  localizedFeatures = [],
  localizedToolIntentLinks = [],
  localizedComparisonIntentLinks = [],
  localizedStudioModules = [],
  localizedPlatformList = [],
  localizedPlatformPresets = [],
  localizedMarketHighlights = [],
  localizedVisualIntelligence = [],
  localizedImageGeneratorHighlights = [],
  localizedMetadataSamples = [],
  localizedComparison,
  localizedSafeguardCards = [],
  localizedTestimonials = [],
  localizedFaqs = [],
  localizedCoreRouteLinks = [],
  trialFlowCopy,
  selectedPlatform,
  setSelectedPlatform,
  trialPlatforms = [],
  trialLoading,
  trialError,
  trialPreviewUrl,
  trialFileName,
  trialImageAlt,
  dragActive,
  setDragActive,
  handleDrop,
  trialInputRef,
  handleTrialFile,
  localizedHelpHref,
  localizedVideoDemoHref,
  blogPosts,
  blogPreviewRef,
  onOpenBlog,
  onOpenBlogIndex,
  isGuest
}) => {
  const ui = publicHomeUiCopy;
  const [activePlatform, setActivePlatform] = useState(0);
  const [activeOutput, setActiveOutput] = useState('CSV');
  const [copiedOutput, setCopiedOutput] = useState('');
  const [comparisonPosition, setComparisonPosition] = useState(50);
  const [comparisonInteracting, setComparisonInteracting] = useState(false);
  const [comparisonShaderReady, setComparisonShaderReady] = useState(false);
  const platformTabRefs = useRef([]);
  const outputTabRefs = useRef([]);
  const comparisonSectionRef = useRef(null);
  const comparisonInteractionTimerRef = useRef(null);
  const studioAssets = useMemo(() => getMetadataStudioThemeAssets(theme), [theme]);
  const localeTag = getLanguageMarket(language)?.localeTag || 'en-US';

  const beginComparisonInteraction = () => {
    if (comparisonInteractionTimerRef.current) {
      window.clearTimeout(comparisonInteractionTimerRef.current);
      comparisonInteractionTimerRef.current = null;
    }
    setComparisonShaderReady(true);
    setComparisonInteracting(true);
  };

  const endComparisonInteraction = () => {
    if (comparisonInteractionTimerRef.current) {
      window.clearTimeout(comparisonInteractionTimerRef.current);
    }
    comparisonInteractionTimerRef.current = window.setTimeout(() => {
      setComparisonInteracting(false);
      comparisonInteractionTimerRef.current = null;
    }, 420);
  };

  useEffect(() => () => {
    if (comparisonInteractionTimerRef.current) {
      window.clearTimeout(comparisonInteractionTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const section = comparisonSectionRef.current;
    if (!section || typeof window === 'undefined') return undefined;

    const desktopQuery = window.matchMedia('(min-width: 621px)');
    let observer = null;

    const observeSection = () => {
      observer?.disconnect();
      observer = null;
      if (!desktopQuery.matches || comparisonShaderReady) return;

      if (!('IntersectionObserver' in window)) {
        setComparisonShaderReady(true);
        return;
      }

      observer = new window.IntersectionObserver(([entry]) => {
        if (!entry?.isIntersecting) return;
        setComparisonShaderReady(true);
        observer?.disconnect();
        observer = null;
      }, {
        rootMargin: '320px 0px',
        threshold: 0.01
      });
      observer.observe(section);
    };

    observeSection();
    desktopQuery.addEventListener?.('change', observeSection);

    return () => {
      observer?.disconnect();
      desktopQuery.removeEventListener?.('change', observeSection);
    };
  }, [comparisonShaderReady]);

  const combinedModules = useMemo(() => {
    const items = [...localizedStudioModules, ...localizedVisualIntelligence];
    const seen = new Set();
    return items.filter((item) => {
      const key = String(item.title || '').trim().toLocaleLowerCase(language);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [language, localizedStudioModules, localizedVisualIntelligence]);

  const outputSample = localizedMetadataSamples[0];
  const outputTextByFormat = useMemo(() => Object.fromEntries(
    OUTPUT_FORMATS.map((format) => [format, exportPreviewCopy(outputSample, format)])
  ), [outputSample]);

  const marketHref = localizeSeoHref('/market-intelligence', language);
  const imageGeneratorHref = localizeSeoHref('/image-generator', language);
  const toolsVisible = localizedToolIntentLinks.slice(0, 8);
  const toolsRemaining = localizedToolIntentLinks.slice(8);
  const marketBoard = homeContent.homepagePanels?.marketBoard || {};
  const comparisonSection = homeContent.comparisonSection || {};

  const activatePlatform = (nextIndex) => {
    if (!localizedPlatformPresets.length) return;
    const normalizedIndex = (nextIndex + localizedPlatformPresets.length) % localizedPlatformPresets.length;
    setActivePlatform(normalizedIndex);
    platformTabRefs.current[normalizedIndex]?.focus();
  };

  const handlePlatformKeyDown = (event, index) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      activatePlatform(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      activatePlatform(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      activatePlatform(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      activatePlatform(localizedPlatformPresets.length - 1);
    }
  };

  const copyOutput = async () => {
    const value = outputTextByFormat[activeOutput] || '';
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    }
    setCopiedOutput(activeOutput);
    window.setTimeout(() => setCopiedOutput(''), 1800);
  };

  return (
    <div className="premium-public-home">
      <section id="guest-hero" className="premium-hero home-band--hero">
        <div className="premium-shell premium-hero-grid">
          <div className="premium-hero-copy" data-home-reveal>
            <p className="premium-eyebrow premium-eyebrow--dark">
              {showAuthenticatedUi ? homeContent.heroPillHome : homeContent.heroPillGuest}
            </p>
            <h1>{homeContent.heroTitle}</h1>
            <p className="premium-hero-description">
              {showAuthenticatedUi ? homeContent.heroSubtitleAuth : homeContent.heroSubtitleGuest}
            </p>
            <div className="premium-hero-actions">
              {renderActionElement({
                href: studioHref,
                onClick: onScrollToStudio,
                className: 'premium-button premium-button--primary',
                children: <><Sparkles size={17} aria-hidden="true" />{homeContent.openMetadataStudio}<ArrowRight size={16} aria-hidden="true" /></>
              })}
              {renderActionElement({
                href: demoHref,
                onClick: onStartDemo,
                className: 'premium-button premium-button--dark-ghost',
                children: <><Play size={16} aria-hidden="true" />{demoStarted ? homeContent.replayDemo : homeContent.runDemo}</>
              })}
            </div>
            <div className="premium-hero-stats" aria-label={homeContent.sectionWhyTitle}>
              {localizedStats.map((stat) => (
                <div key={`${stat.value}-${stat.label}`}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="premium-hero-scene-wrap"
            data-home-reveal
          >
            <div className="premium-hero-scene">
              <div className="premium-scene-bar" aria-hidden="true">
                <span /><span /><span />
                <em>Metadata Studio</em>
              </div>
              {themeReady ? (
                <ProductPicture
                  key={`hero-studio-${theme}`}
                  src={studioAssets.hero.src}
                  srcSet={studioAssets.hero.srcSet}
                  alt={ui.productSceneAlt || studioAssets.hero.alt}
                  width={studioAssets.hero.width}
                  height={studioAssets.hero.height}
                  loading="eager"
                  fetchPriority="high"
                  sizes="(max-width: 760px) calc(100vw - 32px), (max-width: 1180px) 82vw, 760px"
                  className="premium-theme-studio-capture"
                />
              ) : <div className="premium-theme-studio-placeholder" aria-hidden="true" />}
              <HeroMetadataTransformation ui={ui} />
            </div>
          </div>
        </div>
      </section>

      <section id="guest-platform-trust" className="premium-trust-band" aria-labelledby="platform-trust-title">
        <div className="premium-shell" data-home-reveal>
          <p id="platform-trust-title" className="premium-eyebrow">{ui.platformEyebrow}</p>
          <div className="premium-platform-chips">
            {localizedPlatformList.map((platform) => <span key={platform}>{platform}</span>)}
          </div>
        </div>
      </section>

      {homeShowcaseCopy?.intro ? (
        <HomeShowcaseIntro
          copy={homeShowcaseCopy.intro}
          language={language}
          localeTag={localeTag}
          theme={theme}
          studioHref={studioHref}
          openStudioLabel={homeContent.openMetadataStudio}
          onScrollToStudio={onScrollToStudio}
          renderActionElement={renderActionElement}
        />
      ) : null}

      <div id="guest-workflow" className="premium-story-anchor">
        <MetadataStudioStory
          language={language}
          theme={theme}
          themeReady={themeReady}
          copy={metadataStoryCopy}
          studioHref={studioHref}
          onOpenStudio={onScrollToStudio}
        />
        <div className="premium-shell premium-workflow-contract" aria-label={metadataCapabilityContent.flowTitle} data-home-reveal>
          {localizedMetadataFlowSteps.map((step, index) => {
            const Icon = metadataFlowIcons[index] || CheckCircle2;
            return (
              <article key={`${step.step}-${step.title}`}>
                <span aria-hidden="true"><Icon size={19} /></span>
                <p><small>{step.step}</small><strong>{step.title}</strong></p>
                {step.description && <p className="premium-workflow-desc">{step.description}</p>}
                <ul>
                  {step.bullets?.map((bullet) => <li key={bullet}>{bullet}</li>)}
                </ul>
              </article>
            );
          })}
        </div>
      </div>

      <section id="guest-export-readiness" className="premium-section premium-section--dark premium-export-readiness">
        <div className="premium-shell premium-export-layout">
          <div data-home-reveal>
            <SectionIntro
              eyebrow={metadataCapabilityContent.localeEyebrow || ui.exportEyebrow}
              title={metadataCapabilityContent.localeTitle}
              description={metadataCapabilityContent.localeDesc}
            />
            <div className="premium-readiness-list">
              {metadataCapabilityContent.localeLanguages?.map((item) => (
                <div key={item}><ShieldCheck size={19} aria-hidden="true" /><span>{item}</span></div>
              ))}
            </div>
            <p className="premium-note">{metadataCapabilityContent.localeNote}</p>
            <a className="premium-text-link premium-text-link--light" href={localizedHelpHref}>
              {metadataCapabilityContent.helpCta}<ArrowRight size={15} aria-hidden="true" />
            </a>
          </div>
          <div className="premium-export-product" data-home-reveal>
            {themeReady ? (
              <ProductPicture
                key={`export-studio-${theme}`}
                src={studioAssets.finalCta.src}
                srcSet={studioAssets.finalCta.srcSet}
                alt={metadataCapabilityContent.localeTitle || studioAssets.finalCta.alt}
                width={studioAssets.finalCta.width}
                height={studioAssets.finalCta.height}
                className="premium-theme-studio-capture"
              />
            ) : <div className="premium-theme-studio-placeholder" aria-hidden="true" />}
          </div>
        </div>
        {homeShowcaseCopy?.languages ? (
          <div className="premium-shell">
            <HomeShowcaseLanguages
              copy={homeShowcaseCopy.languages}
              language={language}
              localeTag={localeTag}
              theme={theme}
            />
          </div>
        ) : null}
      </section>

      <section id="guest-live-trial" className="premium-section premium-trial-section">
        <div className="premium-shell premium-trial-grid">
          <div>
            <SectionIntro eyebrow={ui.trialEyebrow} title={homeContent.trialTitle} description={homeContent.trialDesc} />
            <p className="premium-trial-limit">{ui.trialLimit}</p>
            <ol className="premium-trial-steps">
              {trialFlowCopy.flowSteps?.map((step, index) => (
                <li key={step}><span>{index + 1}</span>{step}</li>
              ))}
            </ol>
          </div>
          <div className="premium-trial-panel" data-home-reveal>
            <label htmlFor="premium-trial-platform">{homeContent.targetStockSite}</label>
            <select
              id="premium-trial-platform"
              name="trial-platform"
              autoComplete="off"
              value={selectedPlatform}
              onChange={(event) => setSelectedPlatform(event.target.value)}
            >
              {trialPlatforms.map((platform) => <option value={platform.id} key={platform.id}>{platform.label}</option>)}
            </select>
            <button
              type="button"
              className={`premium-dropzone ${dragActive ? 'is-dragging' : ''}`}
              onClick={() => trialInputRef.current?.click()}
              onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
              onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
              }}
              onDrop={handleDrop}
              disabled={trialLoading}
            >
              {trialPreviewUrl ? (
                <img src={trialPreviewUrl} alt={trialImageAlt || homeContent.trialPreviewAlt} />
              ) : (
                <span className="premium-dropzone-icon" aria-hidden="true"><UploadCloud size={30} /></span>
              )}
              {trialLoading ? (
                <LoadingState as="strong" variant="text">
                  {homeContent.generatingMetadata}
                </LoadingState>
              ) : (
                <strong>{homeContent.dropImageToGenerate}</strong>
              )}
              <span>{trialFileName || homeContent.dropSubtitle}</span>
              <em>{homeContent.chooseImage}</em>
            </button>
            <input
              ref={trialInputRef}
              type="file"
              name="trial-image"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleTrialFile(file);
                event.target.value = '';
              }}
            />
            <p className="premium-trial-after">{homeContent.trialAfterNote}</p>
            <div className="premium-live-status" role="status" aria-live="polite">
              {trialLoading ? (
                <LoadingState variant="text">{homeContent.generatingMetadata}</LoadingState>
              ) : (trialError || trialFileName || '')}
            </div>
          </div>
        </div>
      </section>

      <section id="guest-product-video" className="premium-section premium-video-section">
        <div className="premium-shell">
          <SectionIntro
            eyebrow={`${homeVideoCopy.eyebrow || homeContent.quickTour} · ${ui.videoDuration}`}
            title={homeVideoCopy.title}
            description={homeVideoCopy.description}
          />
          <ProductVideoFeature
            video={studioVideo}
            title={homeVideoCopy.title}
            highlights={homeVideoCopy.highlights}
            checkpoints={homeVideoCopy.checkpoints}
            watchLabel={homeVideoCopy.watchLabel}
          />
          <div className="premium-inline-actions">
            {renderActionElement({
              href: tourHref,
              onClick: onOpenTour,
              className: 'premium-button premium-button--primary',
              children: <>{homeContent.quickTour}<ArrowRight size={15} aria-hidden="true" /></>
            })}
            <a className="premium-button premium-button--outline" href={localizedVideoDemoHref}>
              {homeVideoCopy.pageCta || homeContent.openPage}<ExternalLink size={15} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section id="guest-capability-map" className="premium-section premium-capabilities-section">
        <div className="premium-shell">
          <SectionIntro
            eyebrow={ui.capabilityEyebrow}
            title={metadataCapabilityContent.sectionTitle}
            description={metadataCapabilityContent.sectionDesc}
          />
          <div className="premium-highlight-chips">
            {metadataCapabilityContent.highlights?.map((item) => <span key={item}>{item}</span>)}
          </div>
          <div className="premium-capability-grid" data-parity-count="6">
            {localizedMetadataCapabilityCards.map((card, index) => {
              const Icon = metadataCapabilityCardIcons[index] || FileCheck2;
              return (
                <article key={card.title} className="premium-capability-card" data-capability-item={index + 1} data-home-reveal>
                  <span aria-hidden="true"><Icon size={21} /></span>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <ul>{card.bullets?.map((bullet) => <li key={bullet}><Check size={14} aria-hidden="true" />{bullet}</li>)}</ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {homeShowcaseCopy?.reels ? (
        <HomeShowcaseReels
          copy={homeShowcaseCopy.reels}
          language={language}
          localeTag={localeTag}
          theme={theme}
        />
      ) : null}

      <section id="guest-tools" className="premium-section premium-tools-section">
        <span id="guest-core-tools" className="premium-anchor-target" aria-hidden="true" />
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.toolsEyebrow} title={homeContent.guidesTitle} description={homeContent.guidesDesc} />
          <div className="premium-tools-grid">
            {toolsVisible.map((tool) => (
              <a key={tool.href} href={tool.href} className="premium-tool-card" data-home-reveal>
                <span>{tool.pill}</span>
                <h3>{tool.title}</h3>
                <p>{tool.description}</p>
                <em>{homeContent.openPage}<ChevronRight size={14} aria-hidden="true" /></em>
              </a>
            ))}
          </div>
          {toolsRemaining.length ? (
            <details className="premium-tools-disclosure">
              <summary>{ui.exploreAllTools}<span>{toolsRemaining.length}</span></summary>
              <div className="premium-tools-disclosure-grid">
                {toolsRemaining.map((tool) => (
                  <a key={tool.href} href={tool.href}>
                    <strong>{tool.title}</strong><span>{tool.description}</span><ArrowRight size={14} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      </section>

      <section id="guest-market-intelligence" className="premium-section premium-market-section">
        <div className="premium-shell premium-market-layout">
          <div>
            <SectionIntro eyebrow={ui.marketEyebrow} title={homeContent.sectionMarketTitle} description={homeContent.sectionMarketDesc} />
            <div className="premium-market-feature-grid">
              {localizedMarketHighlights.map((item, index) => <IconCard item={item} index={index} key={item.title} />)}
            </div>
            <p className="premium-note">{homeContent.sectionMarketNote}</p>
            <a className="premium-button premium-button--primary" href={marketHref}>{ui.exploreMarket}<ArrowRight size={15} aria-hidden="true" /></a>
          </div>
          <aside className="premium-market-board" aria-label={marketBoard.kicker} data-home-reveal>
            <div className="premium-market-board-head">
              <span><ScanSearch size={18} aria-hidden="true" />{marketBoard.kicker}</span>
              <em>{ui.sampleSignal}</em>
            </div>
            <div className="premium-signal-list">
              {marketBoard.trendSignals?.map((signal) => (
                <div key={signal.label}><span>{signal.label}</span><strong>{signal.value}</strong></div>
              ))}
            </div>
            <div className="premium-market-insight">
              <span>{marketBoard.creatorKicker}</span>
              <h3>{marketBoard.insightTitle}</h3>
              <p>{marketBoard.insightDescription}</p>
            </div>
          </aside>
        </div>
      </section>

      <section id="guest-studio-modules" className="premium-section premium-section--warm">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.modulesEyebrow} title={homeContent.sectionModulesTitle} description={homeContent.sectionModulesDesc} />
          <div className="premium-modules-grid" data-parity-count="11">
            {combinedModules.map((module, index) => <IconCard item={module} index={index} key={module.title} />)}
          </div>
        </div>
      </section>

      <section id="guest-platform-exports" className="premium-section premium-platform-tabs-section">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.exportsEyebrow} title={homeContent.sectionExportsTitle} description={homeContent.sectionExportsDesc} />
          <div className="premium-platform-tabs" data-home-reveal>
            <div className="premium-tab-list" role="tablist" aria-label={homeContent.sectionPlatformIntelTitle}>
              {localizedPlatformPresets.map((preset, index) => (
                <button
                  key={preset.name}
                  ref={(node) => { platformTabRefs.current[index] = node; }}
                  id={`premium-platform-tab-${index}`}
                  role="tab"
                  type="button"
                  tabIndex={activePlatform === index ? 0 : -1}
                  aria-selected={activePlatform === index}
                  aria-controls={`premium-platform-panel-${index}`}
                  onClick={() => setActivePlatform(index)}
                  onKeyDown={(event) => handlePlatformKeyDown(event, index)}
                >
                  {preset.name}
                </button>
              ))}
            </div>
            {localizedPlatformPresets.map((preset, index) => (
              <div
                key={`${preset.name}-panel`}
                id={`premium-platform-panel-${index}`}
                role="tabpanel"
                aria-labelledby={`premium-platform-tab-${index}`}
                hidden={activePlatform !== index}
                className="premium-platform-panel"
              >
                <div>
                  <p className="premium-eyebrow">{homeContent.presetLabel}</p>
                  <h3>{preset.name}</h3>
                  <p>{preset.description}</p>
                  <ul>{preset.bullets?.map((bullet) => <li key={bullet}><Check size={14} aria-hidden="true" />{bullet}</li>)}</ul>
                </div>
                <div className="premium-platform-sample">
                  <span>{homeContent.sampleOutput}</span>
                  <strong>{preset.sample?.title}</strong>
                  <div>{preset.sample?.tags?.map((tag) => <em key={tag}>{tag}</em>)}</div>
                </div>
              </div>
            ))}
            <div className="premium-all-platforms" aria-label={ui.platformEyebrow}>
              {localizedPlatformList.map((platform) => <span key={platform}>{platform}</span>)}
            </div>
          </div>
        </div>
      </section>

      {isUnlimitedPlan && (
      <section id="guest-image-generator" className="premium-section premium-section--dark premium-generator-section">
        <div className="premium-shell">
          <div className="premium-generator-layout">
            <div>
            <SectionIntro eyebrow={ui.generatorEyebrow} title={homeContent.sectionImageGenTitle} description={homeContent.sectionImageGenDesc} />
            <div className="premium-generator-flow" role="group" aria-label={homeContent.sectionImageGenTitle}>
              <span>OpenAI / Gemini</span><ArrowRight size={15} aria-hidden="true" /><span>{ui.generateStep}</span><ArrowRight size={15} aria-hidden="true" /><span>{ui.tagStep}</span><ArrowRight size={15} aria-hidden="true" /><span>{ui.exportStep}</span>
            </div>
            <p className="premium-note">{homeContent.sectionImageGenNote}</p>
            <a className="premium-button premium-button--primary" href={imageGeneratorHref}>{ui.openGenerator}<ArrowRight size={15} aria-hidden="true" /></a>
            </div>
            <div className="premium-ai-bridge" data-home-reveal role="group" aria-label={homeContent.sectionImageGenTitle}>
              <article className="premium-ai-canvas">
                <div className="premium-ai-canvas-head"><span>{ui.generatorEyebrow}</span><em>OpenAI / Gemini</em></div>
                <img
                  src="/home-redesign/story/optimized/sustainable-city-vector-640.webp"
                  alt={ui.aiBridgeAlt}
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                />
                <div className="premium-ai-controls"><span>1:1</span><span>PNG</span><span>{ui.generateStep}</span></div>
              </article>
              <div className="premium-ai-handoff" aria-hidden="true">
                <span>{ui.generateStep}</span><ArrowRight size={15} /><span>{ui.tagStep}</span><ArrowRight size={15} /><span>{ui.exportStep}</span>
              </div>
              <article className="premium-ai-metadata">
                <span className="premium-ai-metadata-status"><CheckCircle2 size={14} aria-hidden="true" />Metadata Studio</span>
                <strong>{homeContent.sectionOutputsTitle}</strong>
                <div className="premium-ai-metadata-fields">
                  <span>{homeContent.labelTitle}</span><i />
                  <span>{homeContent.labelDescription}</span><i />
                  <span>{homeContent.labelKeywords}</span><i /><i /><i />
                </div>
                <em><FileSpreadsheet size={14} aria-hidden="true" />CSV + XMP</em>
              </article>
            </div>
          </div>
          <div className="premium-generator-grid premium-generator-grid--rail" data-home-reveal>
            {localizedImageGeneratorHighlights.map((item, index) => <IconCard item={item} index={index} key={item.title} />)}
          </div>
        </div>
      </section>
      )}

      <section id="guest-outputs" className="premium-section premium-outputs-section">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.outputsEyebrow} title={homeContent.sectionOutputsTitle} description={homeContent.sectionOutputsDesc} />
          <div className="premium-output-workbench" data-home-reveal>
            <div className="premium-output-tabs">
              <div className="premium-output-tablist" role="tablist" aria-label={ui.formatPreview}>
                {OUTPUT_FORMATS.map((format, formatIndex) => {
                  const Icon = format === 'CSV' ? FileSpreadsheet : format === 'XMP' ? FileText : FileJson2;
                  return (
                    <button
                      key={format}
                      ref={(node) => { outputTabRefs.current[formatIndex] = node; }}
                      id={`premium-output-tab-${format.toLowerCase()}`}
                      role="tab"
                      type="button"
                      aria-selected={activeOutput === format}
                      aria-controls={`premium-output-panel-${format.toLowerCase()}`}
                      tabIndex={activeOutput === format ? 0 : -1}
                      onClick={() => setActiveOutput(format)}
                      onKeyDown={(event) => {
                        const currentIndex = OUTPUT_FORMATS.indexOf(format);
                        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft' || event.key === 'Home' || event.key === 'End') {
                          event.preventDefault();
                          const nextIndex = event.key === 'Home'
                            ? 0
                            : event.key === 'End'
                              ? OUTPUT_FORMATS.length - 1
                              : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + OUTPUT_FORMATS.length) % OUTPUT_FORMATS.length;
                          const next = OUTPUT_FORMATS[nextIndex];
                          setActiveOutput(next);
                          outputTabRefs.current[nextIndex]?.focus();
                        }
                      }}
                    >
                      <Icon size={16} aria-hidden="true" />{format}
                    </button>
                  );
                })}
              </div>
              <button type="button" className="premium-copy-button" onClick={copyOutput}>
                {copiedOutput === activeOutput ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                {copiedOutput === activeOutput ? ui.copied : ui.copy}
              </button>
            </div>
            {OUTPUT_FORMATS.map((format) => (
              <div
                key={format}
                id={`premium-output-panel-${format.toLowerCase()}`}
                role="tabpanel"
                aria-labelledby={`premium-output-tab-${format.toLowerCase()}`}
                hidden={activeOutput !== format}
              >
                <pre tabIndex="0"><code>{outputTextByFormat[format]}</code></pre>
              </div>
            ))}
            <p>{ui.previewOnly}</p>
            <span className="premium-live-status" aria-live="polite">{copiedOutput ? `${copiedOutput} · ${ui.copied}` : ''}</span>
          </div>
          <div className="premium-metadata-samples">
            {localizedMetadataSamples.map((sample) => (
              <article key={`${sample.mediaLabel}-${sample.title}`} className="premium-metadata-card">
                <div className="premium-metadata-media">
                  {sample.mediaType === 'video' ? (
                    <video src={sample.mediaSrc} poster={sample.mediaPoster} muted playsInline preload="none" aria-label={sample.mediaAlt} />
                  ) : (
                    <img src={sample.mediaSrc} alt={sample.mediaAlt} width={sample.mediaWidth} height={sample.mediaHeight} loading="lazy" decoding="async" />
                  )}
                  <span>{sample.mediaLabel}</span>
                </div>
                <div className="premium-metadata-copy">
                  <strong>{sample.title}</strong>
                  <p>{sample.description}</p>
                  <div>{sample.keywords?.map((keyword) => <span key={keyword}>{keyword}</span>)}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="guest-why-stocktag" className="premium-section premium-why-section">
        <div className="premium-shell premium-why-layout">
          <div>
            <SectionIntro eyebrow={ui.whyEyebrow} title={homeContent.sectionWhyTitle} description={homeContent.sectionWhyDesc} />
            <div className="premium-benefit-list">
              {localizedFeatures.map((feature, index) => {
                const Icon = feature.icon || CheckCircle2;
                return (
                  <article key={feature.title}>
                    <span aria-hidden="true"><Icon size={18} /></span>
                    <div><h3>{feature.title}</h3><p>{feature.description}</p></div>
                  </article>
                );
              })}
            </div>
          </div>
          <div className="premium-why-product" data-home-reveal>
            {themeReady ? (
              <>
                <ProductPicture
                  key={`why-keywords-${theme}`}
                  src={studioAssets.captures.keywords.src}
                  srcSet={studioAssets.captures.keywords.srcSet}
                  alt={ui.workflowImages[2] || studioAssets.captures.keywords.alt}
                  width={studioAssets.captures.keywords.width}
                  height={studioAssets.captures.keywords.height}
                  className="premium-theme-studio-capture"
                />
                <ProductPicture
                  key={`why-review-${theme}`}
                  src={studioAssets.captures.review.src}
                  srcSet={studioAssets.captures.review.srcSet}
                  alt={localizedSafeguardCards[3]?.title || studioAssets.captures.review.alt}
                  width={studioAssets.captures.review.width}
                  height={studioAssets.captures.review.height}
                  className="premium-theme-studio-capture"
                />
              </>
            ) : <div className="premium-theme-studio-placeholder" aria-hidden="true" />}
          </div>
        </div>
      </section>

      <section id="guest-comparison" className="premium-section premium-comparison-section">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.compareEyebrow} title={comparisonSection.title} description={comparisonSection.subtitle} />
          <div className="premium-comparison-table" role="table" aria-label={comparisonSection.title} data-parity-count="9">
            <div className="premium-comparison-header" role="row">
              <span role="columnheader">{comparisonSection.featureHeader}</span>
              <span role="columnheader">{ui.stocktagLabel}</span>
              <span role="columnheader">{ui.genericLabel}</span>
            </div>
            {comparisonSection.rows?.map((row, index) => (
              <div className="premium-comparison-row" role="row" key={row.featureTitle} data-comparison-row={index + 1}>
                <div role="cell"><strong>{row.featureTitle}</strong><span>{row.featureDesc}</span></div>
                <div role="cell" className="is-stocktag"><strong><CheckCircle2 size={15} aria-hidden="true" />{row.stockStatus}</strong><span>{row.stockDesc}</span></div>
                <div role="cell"><strong>{row.genericStatus}</strong><span>{row.genericDesc}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="guest-before-after"
        ref={comparisonSectionRef}
        className="premium-section premium-before-after-section"
      >
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.beforeAfterEyebrow} title={homeContent.beforeAfterSr} />
          <div className="premium-before-after-control">
            <label htmlFor="premium-comparison-range">{homeContent.beforeAfterSr}</label>
            <input
              id="premium-comparison-range"
              type="range"
              min="20"
              max="80"
              value={comparisonPosition}
              onChange={(event) => setComparisonPosition(Number(event.target.value))}
              aria-valuetext={`${ui.beforeLabel} ${comparisonPosition}% · ${ui.afterLabel} ${100 - comparisonPosition}%`}
              onPointerDown={beginComparisonInteraction}
              onPointerUp={endComparisonInteraction}
              onPointerCancel={endComparisonInteraction}
              onLostPointerCapture={endComparisonInteraction}
              onKeyDown={(event) => {
                if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
                  beginComparisonInteraction();
                }
              }}
              onKeyUp={endComparisonInteraction}
              onBlur={endComparisonInteraction}
            />
          </div>
          <div
            className="premium-before-after"
            data-comparison-interacting={comparisonInteracting ? 'true' : 'false'}
            style={{ '--comparison-position': `${comparisonPosition}%` }}
          >
            <article className="premium-before-card">
              <span><Clipboard size={18} aria-hidden="true" />{ui.beforeLabel}</span>
              <div className="premium-before-visual" aria-hidden="true">
                <div className="premium-workflow-assets">
                  <figure>
                    <img src={COMPARISON_HIKER_SRC} alt="" width="640" height="427" loading="lazy" />
                  </figure>
                  <img src="/home-redesign/story/optimized/reusable-bottle-320.webp" alt="" width="320" height="480" loading="lazy" />
                  <img src="/home-redesign/story/optimized/sustainable-city-vector-320.webp" alt="" width="320" height="320" loading="lazy" />
                </div>
                <div className="premium-manual-hops"><span><FileText size={13} /></span><ArrowRight size={13} /><span><Copy size={13} /></span><ArrowRight size={13} /><span><FileSpreadsheet size={13} /></span></div>
              </div>
              <ul>{localizedComparison?.old?.map((item) => <li key={item}>{item}</li>)}</ul>
            </article>
            <article className="premium-after-card">
              <span><Sparkles size={18} aria-hidden="true" />{ui.afterLabel}</span>
              <div className="premium-after-visual" aria-hidden="true">
                <div className="premium-workflow-assets">
                  <figure><img src={COMPARISON_HIKER_SRC} alt="" width="640" height="427" loading="lazy" /><Check size={11} /></figure>
                  <figure><img src="/home-redesign/story/optimized/reusable-bottle-320.webp" alt="" width="320" height="480" loading="lazy" /><Check size={11} /></figure>
                  <figure><img src="/home-redesign/story/optimized/sustainable-city-vector-320.webp" alt="" width="320" height="320" loading="lazy" /><Check size={11} /></figure>
                </div>
                <div className="premium-ready-line"><CheckCircle2 size={13} /><span>3 / 3</span><strong>CSV + XMP</strong></div>
              </div>
              <ul>{localizedComparison?.studio?.map((item) => <li key={item}><Check size={14} aria-hidden="true" />{item}</li>)}</ul>
            </article>
            {comparisonShaderReady ? (
              <div
                className={`premium-comparison-fluted-sweep ${comparisonInteracting ? 'is-active' : ''}`}
                data-comparison-effect="fluted-glass"
                aria-hidden="true"
              >
                <HomeFlutedGlass
                  className="premium-comparison-fluted-surface"
                  maxPixelCount={90000}
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section id="guest-safeguards" className="premium-section premium-trust-section">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.trustEyebrow} title={homeContent.sectionSafeguardsTitle} description={homeContent.sectionSafeguardsDesc} />
          <div className="premium-safeguard-grid">
            {localizedSafeguardCards.map((card, index) => (
              <article key={card.title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{card.title}</h3><p>{card.description}</p></article>
            ))}
          </div>
          <div className="premium-testimonial-heading">
            <p className="premium-eyebrow">{ui.testimonialsEyebrow}</p>
            <h2>{homeContent.sectionFeedbackTitle}</h2>
            <p>{homeContent.sectionFeedbackDesc}</p>
          </div>
          <div className="premium-testimonial-grid">
            {localizedTestimonials.map((testimonial, index) => (
              <figure key={`${testimonial.author}-${index}`}>
                <blockquote>“{testimonial.quote}”</blockquote>
                <figcaption><strong>{testimonial.author}</strong><span>{testimonial.role}</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="guest-close" className="premium-section premium-pricing-section">
        <div className="premium-shell">
          <SectionIntro
            eyebrow={ui.pricingEyebrow}
            title={isFreeMember ? salesCopy.stripTitleFree : salesCopy.stripTitleGuest}
            description={isFreeMember ? salesCopy.stripBodyFree : salesCopy.stripBodyGuest}
            align="center"
          />
          <div className="premium-pricing-grid">
            {salesCopy.cards?.map((card, index) => (
              <article key={card.title} className={index === 1 ? 'is-featured' : ''}>
                <span>{card.eyebrow}</span><h3>{card.title}</h3><strong>{card.value}</strong><p>{card.note}</p>
              </article>
            ))}
          </div>
          <ul className="premium-pricing-bullets">
            {salesCopy.bullets?.map((bullet) => <li key={bullet}><CheckCircle2 size={16} aria-hidden="true" />{bullet}</li>)}
          </ul>
          <div className="premium-pricing-actions">
            {showPricingPrompt ? renderActionElement({
              href: pricingHref,
              onClick: onOpenPricing,
              className: 'premium-button premium-button--primary',
              children: <>{salesCopy.primaryAction}<ArrowRight size={15} aria-hidden="true" /></>
            }) : null}
            {renderActionElement({
              href: studioHref,
              onClick: onScrollToStudio,
              className: 'premium-button premium-button--outline',
              children: salesCopy.secondaryAction
            })}
          </div>
          <p className="premium-pricing-footer">{salesCopy.footer}</p>
        </div>
      </section>

      <section id="guest-faq" className="premium-section premium-faq-section">
        <div className="premium-shell premium-faq-layout">
          <SectionIntro eyebrow={ui.faqEyebrow} title={homeContent.sectionFaqTitle} description={homeContent.sectionFaqDesc} />
          <div className="premium-faq-list" data-parity-count="12">
            {localizedFaqs.map((item, index) => (
              <details key={item.question} data-faq-item={index + 1}>
                <summary><span>{item.question}</span><span aria-hidden="true">+</span></summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="guest-blog" className="premium-section premium-blog-section" ref={blogPreviewRef}>
        <div className="premium-shell">
          <GuestBlogPreview
            posts={blogPosts}
            onOpenBlog={onOpenBlog}
            onOpenBlogIndex={onOpenBlogIndex}
            isGuest={isGuest}
            variant="compact"
            homeContent={homeContent}
          />
        </div>
      </section>

      <section id="guest-routes" className="premium-section premium-routes-section">
        <div className="premium-shell">
          <SectionIntro eyebrow={ui.routesEyebrow} title={homeContent.sectionRoutesTitle} description={homeContent.sectionRoutesDesc} />
          <nav className="premium-core-routes" aria-label={homeContent.sectionRoutesTitle} data-parity-count="17">
            {localizedCoreRouteLinks.map((route, index) => (
              <a href={route.href} key={route.href} data-core-route={index + 1}>{route.label}<ArrowRight size={13} aria-hidden="true" /></a>
            ))}
          </nav>
          {localizedComparisonIntentLinks.length ? (
            <div className="premium-compare-links">
              <h2>{homeContent.sectionCompareTitle}</h2>
              <p>{homeContent.sectionCompareDesc}</p>
              <div>
                {localizedComparisonIntentLinks.map((item) => (
                  <a key={item.href} href={item.href}><strong>{item.title}</strong><span>{item.description}</span><ArrowRight size={14} aria-hidden="true" /></a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default PremiumPublicHomeLanding;
